// Agrupación del formulario multimodal de mercancías peligrosas.
//
// Espejo de `_agrupar_multimodal` del backend, que es la autoridad: el PDF lo
// construye él a partir del mismo payload. Vive aquí porque la pantalla tiene
// que enseñar las MISMAS filas que se van a firmar, y no las líneas sueltas del
// packing — un documento de mercancía peligrosa se revisa por mercancía, no por
// artículo.
//
// Este documento no tiene datos propios: se sirve de las líneas del packing y
// lee otras columnas de la misma fila.
import type { MultimodalGrupo, PackingListLinea } from '@/types'

const dec2 = (n: number) => Math.round(n * 100) / 100
const dec3 = (n: number) => Math.round(n * 1000) / 1000

/** Agrupa las líneas por CLASIFICACIÓN, como el PDF.
 *
 *  La clave es la clasificación completa (ONU + clase + grupo de embalaje +
 *  punto de inflamación + cantidades limitadas + nombre y tipos en inglés), no
 *  el número ONU solo: dos artículos con el mismo número pero distinto grupo de
 *  embalaje son dos mercancías distintas en el documento.
 *
 *  Las líneas sin `onu` se quedan fuera: no son mercancía peligrosa. Es lo
 *  normal — la mayoría de una carga no lo es.
 *
 *  El bruto es neto + tara de palet, con la tara contada UNA vez por matrícula
 *  dentro de cada grupo. En un palet MIXTO su tara cae en más de un grupo y se
 *  cuenta en cada uno, así que la suma puede superar el bruto del packing de la
 *  misma carga (medido en el contrato de ejemplo: 3.322,82 contra 3.320,00). Son
 *  dos definiciones legítimas de «bruto», no un descuadre. */
export function agruparMultimodal(lineas: PackingListLinea[]): MultimodalGrupo[] {
  const grupos = new Map<string, MultimodalGrupo>()
  // Taras ya contadas, por (grupo, matrícula).
  const taras = new Set<string>()

  for (const l of lineas) {
    if (!l.onu) continue
    const clave = [
      l.onu, l.clase, l.grupoEmbalaje, l.puntoInflamacion,
      l.cantidadesLimitadas, l.nombreExpedicionEn, l.tipoBultoEn, l.tipoUnidadEn,
    ].join('|')

    let g = grupos.get(clave)
    if (!g) {
      g = {
        onu: l.onu, clase: l.clase, grupoEmbalaje: l.grupoEmbalaje,
        puntoInflamacion: l.puntoInflamacion,
        nombreExpedicion: l.nombreExpedicionEs || l.nombreExpedicionEn,
        articulos: [], bultos: 0, unidades: 0, pesoNeto: 0, pesoBruto: 0, volumen: 0,
      }
      grupos.set(clave, g)
    }

    if (!g.articulos.includes(l.articulo)) g.articulos.push(l.articulo)
    const cajas = l.cantidadCajas ?? 0
    const neto = (l.pesoCaja ?? 0) * cajas
    g.bultos += cajas
    g.unidades += l.unidades
    g.pesoNeto += neto
    g.pesoBruto += neto
    g.volumen += (l.volumen ?? 0) * cajas

    const tara = `${clave}|${l.matriculaPalet ?? ''}`
    if (l.matriculaPalet && !taras.has(tara)) {
      taras.add(tara)
      g.pesoBruto += l.pesoPalet ?? 0
    }
  }

  return [...grupos.values()].map(g => ({
    ...g,
    pesoNeto: dec2(g.pesoNeto),
    pesoBruto: dec2(g.pesoBruto),
    volumen: dec3(g.volumen),
  }))
}

/** Los artículos que el multimodal deja fuera por no ser mercancía peligrosa.
 *
 *  El documento declara menos referencias que el packing de la misma carga, y
 *  eso hay que poder verlo antes de firmarlo. */
export function articulosSinOnu(lineas: PackingListLinea[]): string[] {
  return [...new Set(lineas.filter(l => !l.onu).map(l => l.articulo))].sort()
}

/** ¿Se puede generar el multimodal de esta carga?
 *
 *  Dos motivos por los que no, y ninguno se arregla tecleando:
 *
 *  - `aerea`: el IMDG es marítimo. Lo aéreo va por IATA DGR y su documento es la
 *    DGD, que es otro formulario.
 *  - `sinMercancia`: si ninguna línea trae número ONU no hay nada que declarar.
 *    En `avp` no lo trae ninguna, porque el maestro de mercancía peligrosa está
 *    vacío en esa empresa.
 *
 *  El backend lo vuelve a comprobar y es quien decide; esto es para que el botón
 *  responda al vuelo y diga por qué. */
export function motivoSinMultimodal(
  lineas: PackingListLinea[],
  esCargaAerea: boolean,
): 'aerea' | 'sinMercancia' | null {
  if (esCargaAerea) return 'aerea'
  return lineas.some(l => l.onu) ? null : 'sinMercancia'
}

export interface TotalesMultimodal {
  mercancias: number
  bultos: number
  pesoNeto: number
  pesoBruto: number
  volumen: number
}

export function totalesMultimodal(grupos: MultimodalGrupo[]): TotalesMultimodal {
  return grupos.reduce<TotalesMultimodal>(
    (a, g) => ({
      mercancias: a.mercancias + 1,
      bultos: a.bultos + g.bultos,
      pesoNeto: dec2(a.pesoNeto + g.pesoNeto),
      pesoBruto: dec2(a.pesoBruto + g.pesoBruto),
      volumen: dec3(a.volumen + g.volumen),
    }),
    { mercancias: 0, bultos: 0, pesoNeto: 0, pesoBruto: 0, volumen: 0 },
  )
}
